import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchFacets } from '@app/src/api/fetchHooks'
import LinkButton from '@app/src/components/LinkButton'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { AssessmentTemplate } from '@app/src/types/resourceExplorer'
import paths from '@app/src/wf-constants/paths'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Paper, Skeleton, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { generatePath } from 'react-router'
import { AssessmentViews } from '../Assessment/assessmentViews'
import AssessmentOverviewGraph from './AssessmentOverviewGraph'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'

type AssessmentOverviewGraphWithHeaderProps = {
  assessmentTemplate: AssessmentTemplate
  userFilters?: FilterGroup[]
}

const AssessmentOverviewGraphWithHeader: React.FC<AssessmentOverviewGraphWithHeaderProps> = ({
  assessmentTemplate,
  userFilters,
}) => {
  const { formatMessage } = useIntl()

  const {
    facets: [periodFacets = []],
    isFetched: isPeriodFacetsFetched,
  } = useFetchFacets({
    key: [FetchKey.AssessmentFacets, 'periods'],
    endpoint: endpoints.assessmentFacet,
    facetsParam: [{ name: 'periodName', isEnum: true }],
    filter: [
      ...(userFilters ?? []),
      { name: 'assessmentTemplateId', filters: [{ value: assessmentTemplate.id, operator: Operators.EqualTo }] },
    ],
    options: { enabled: Boolean(userFilters?.length), staleTime: ONE_MINUTE_MS },
  })

  const {
    facets: [providerFacets],
    isFetched: isProviderFacetsFetched,
  } = useFetchFacets({
    key: [FetchKey.AssessmentFacets, 'providers'],
    endpoint: endpoints.assessmentFacet,
    facetsParam: [{ name: 'providerId', isEnum: true }],
    filter: [
      ...(userFilters ?? []),
      { name: 'assessmentTemplateId', filters: [{ value: assessmentTemplate.id, operator: Operators.EqualTo }] },
    ],
    options: { enabled: Boolean(userFilters?.length), staleTime: ONE_MINUTE_MS },
  })

  const providersCount = providerFacets?.length

  if (!isPeriodFacetsFetched || !isProviderFacetsFetched) {
    return (
      <Stack bgcolor="grey.200" borderRadius={1} p={4} gap={4} minHeight={700} height="100%" flexGrow={1}>
        <Typography variant="h2">{assessmentTemplate.name}</Typography>

        <Stack flexGrow={1} alignItems="flex-end" justifyContent="stretch" width="100%">
          <Skeleton variant="rounded" width="100%" height="100%" />
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack bgcolor={!periodFacets.length ? 'grey.200' : 'common.white'} borderRadius={1} p={4} gap={3} height="100%">
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Typography variant="h2" pr={3}>
          {assessmentTemplate.name}
        </Typography>

        {Boolean(providersCount) && (
          <LinkButton
            variant="text"
            endIcon={<OpenInNewIcon />}
            to={generatePath(paths.assessments, {
              view: AssessmentViews.Overview,
              assessmentTemplateId: assessmentTemplate.id,
            })}
            sx={{ flexShrink: 0 }}
          >
            {formatMessage({ id: 'assessments.overviewGraph.exploreResult' })}
          </LinkButton>
        )}
      </Stack>

      {Boolean(providersCount) && (
        <Stack>
          <Typography>{formatMessage({ id: 'assessments.suppliersAssessed' })}</Typography>
          <Typography variant="subtitle1">{providersCount}</Typography>
        </Stack>
      )}

      <Paper
        elevation={0}
        variant={providersCount ? 'outlined' : undefined}
        sx={({ palette }) =>
          providersCount ? { padding: 2, outline: `1px solid ${palette.grey[300]}`, borderRadius: 2 } : {}
        }
      >
        <AssessmentOverviewGraph assessmentTemplate={assessmentTemplate} userFilters={userFilters} />
      </Paper>
    </Stack>
  )
}

export default AssessmentOverviewGraphWithHeader
